export interface AiImageEditCapability {
  enabled: boolean;
  eligible: boolean;
  dailyLimit: number;
  remaining: number;
}

export interface AiImageEditResult {
  id: number;
  url: string;
  fileName: string;
  contentType: string;
  size: string;
  dir: string;
}

export type AiImageEditTaskState = 'pending' | 'processing' | 'success' | 'failed';

export interface AiImageEditTaskStatus {
  taskId: string;
  status: AiImageEditTaskState;
  result?: AiImageEditResult;
  errorCode?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  code?: string;
  data: T;
}

interface AcceptedTask {
  taskId: string;
  status: 'pending';
}

export class AiImageEditApiError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = 'AiImageEditApiError';
    this.code = code;
  }
}

const parseResponse = async <T>(response: Response): Promise<T> => {
  let result: ApiResponse<T>;
  try {
    result = await response.json();
  } catch {
    throw new AiImageEditApiError('AI image edit service returned an invalid response');
  }
  if (!response.ok || !result.success) {
    throw new AiImageEditApiError(result.message || 'AI image edit request failed', result.code);
  }
  return result.data;
};

export const getAiImageEditCapability = async (): Promise<AiImageEditCapability> => {
  const response = await fetch('/api/image-processing/ai-edit/capability', {
    credentials: 'include',
    cache: 'no-store',
  });
  return parseResponse<AiImageEditCapability>(response);
};

export const submitAiImageEditTask = async (
  uploadRecordId: number,
  prompt: string,
  transparentBackground: boolean,
): Promise<AcceptedTask> => {
  const response = await fetch('/api/image-processing/ai-edit/tasks', {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ uploadRecordId, prompt, transparentBackground }),
  });
  return parseResponse<AcceptedTask>(response);
};

export const getAiImageEditTaskStatus = async (taskId: string): Promise<AiImageEditTaskStatus> => {
  const response = await fetch(`/api/image-processing/ai-edit/tasks/${encodeURIComponent(taskId)}`, {
    credentials: 'include',
  });
  return parseResponse<AiImageEditTaskStatus>(response);
};
